// ICD-S04 — what a notification is, on the wire.
//
// The vocabulary is CLOSED on the server and additive over time: a new row in
// the matrix is a new member here, an old member is never removed while a
// stored notification carries it. The web client already treats a member it
// does not know as "reload the app" rather than as drift (data/api.ts), which
// is exactly the right reading of a newer server.
//
// The copy is not the fact. Every notification carries `type` and typed
// `payload` fields; `title`, `body` and `navigate` are rendered from them
// once, at creation, so every channel and every client shows the same words
// (API.md §1.4: a sentence is never the sole carrier).

import { z } from 'zod';
import { pageOf } from './envelope';
import { NotificationKind } from './notification-kinds';

/** The matrix's rows, NOTIFICATIONS.md §3. Account mail is not here: it bypasses the matrix. */
export const NOTIFICATION_TYPES = [
  // §3.2 league and people
  'league.joined',
  'league.seat_opened',
  'league.removed',
  'league.rules_changed',
  'league.override',
  'league.reversal',
  'league.waivers',
  'league.vote_opened',
  'league.election_opened',
  'league.election_resolved',
  // A scheduled job that failed, to the commissioners (N5, 2026-10-02).
  'league.job_failed',
  // A week settled without some of its games' stat lines, to the
  // commissioners (B-474, the audit of 2026-10-20).
  'league.stats_missing',
  // A commissioner started the league's next season, to everyone carried
  // into it but them (PLAN-2026-10-27-the-next-season §2.1).
  'league.next_season',
  // The site's private question to a manager not seen for a while (J3,
  // PLAN-2026-11-01-the-quiet-manager-check-in): to that person alone.
  'seat.checkin',
  // §3.3 draft
  'draft.scheduled',
  'draft.reminder',
  'draft.plan_missing',
  'draft.on_the_clock',
  'draft.slow_turn',
  // A slow auction's: somebody bid more for a player you were ahead on (AUCTION.md §5).
  'draft.outbid',
  'draft.finished',
  'draft.failed',
  // §3.4 lineup
  'lineup.problem',
  'lineup.starter_news',
  'lineup.nudge',
  // §3.5 waivers and trades
  'waivers.processed',
  'trade.offered',
  'trade.answered',
  'trade.withdrawn',
  'trade.review',
  'trade.processed',
  'trade.vetoed',
  // §3.6 the week
  'matchup.final',
  'recap.ready',
  'award.earned',
  'stats.corrected',
  'playoffs.clinched',
  'playoffs.eliminated',
  'chat.mention',
  'chat.message',
  // §3.7 players you watch (E5, 2026-10-03)
  'watch.status',
  'watch.dropped',
  // §3.8 your achievements (A4, 2026-10-04): one found, or a backfill's finds together.
  'achievement.unlocked',
  // §3.9 the site, to its admins: a game's stat lines an hour past due
  // (DIRECTION D11, 2026-10-21).
  'admin.stats_late',
] as const;
export const NotificationType = z.enum(NOTIFICATION_TYPES);
export type NotificationTypeId = (typeof NOTIFICATION_TYPES)[number];

/** A fact on a notification: a name, a count, a time, a flag. Never a nested object. */
export const NotificationFact = z.union([z.string(), z.number(), z.boolean(), z.null()]);
export const NotificationPayload = z.record(z.string(), NotificationFact);

/**
 * What a producer hands the notifier.
 *
 * `key` is the event's natural key WITHOUT the recipient — `trade.offered|trd-1`,
 * `lineup.problem|lg-1|2026-w03|120` — and the store combines it with each
 * recipient's id. Two producers describing one fact produce one record per
 * person, which is what makes a double-fired job harmless.
 */
export const NotificationEvent = z.object({
  type: NotificationType,
  leagueId: z.string().min(1).nullable(),
  key: z.string().min(1).max(200),
  payload: NotificationPayload,
  /** When it became true. ISO. */
  at: z.string().min(1),
});

export const NotificationView = z.object({
  id: z.string(),
  type: NotificationType,
  leagueId: z.string().nullable(),
  title: z.string(),
  body: z.string(),
  /** Where a tap lands, as an app path: `/l/lg-1/lineup?period=3`. */
  navigate: z.string(),
  lockCritical: z.boolean(),
  payload: NotificationPayload,
  createdAt: z.string(),
  readAt: z.string().nullable(),
  /**
   * The bell's kind it sorts under (`kindOf`, PLAN-2026-10-18-families-and-newcomers
   * §6.1): your league, your team, awards, achievements, chat, players you
   * watch. Added 2026-10-18.
   */
  kind: NotificationKind,
  /**
   * A league's week folded into this one row (§6.2): every row it stands for,
   * this one among them, to read together when it is opened. Empty for a row
   * that stands for itself alone. Added 2026-10-18.
   */
  groupIds: z.array(z.string()).default([]),
});
/**
 * How long the record keeps a notice, from the time it carries (the one a
 * list shows beside it): a read one `readDays`, an unread one `unreadDays`,
 * then it is deleted (the 2026-10-05
 * decision, DATA_LAYER_DESIGN §4.9). The ingest worker's daily retention
 * deletes past it; every page carries it, so a client says what the server
 * does rather than a number of its own.
 */
export const NoticesKept = z.object({
  readDays: z.number().int().positive(),
  unreadDays: z.number().int().positive(),
});
export type NoticesKeptShape = z.infer<typeof NoticesKept>;
export const NOTICES_KEPT: NoticesKeptShape = { readDays: 30, unreadDays: 90 };

/**
 * How long after its event a notice that can be asked for again is sent: a
 * week's recap and awards, which a commissioner's fix to the week mints again.
 * Inside `NOTICES_KEPT.readDays`, so the kept notice's key still stops the
 * repeat; past it, the fix tells the league in its own notice.
 */
export const NOTICE_RESEND_WITHIN_DAYS = 14;

/**
 * A page of the record, newest first, and `mark`: how far this person's record
 * went when the page was read, IN THE ORDER IT WAS WRITTEN. Opaque; hand it
 * back as `{ through: mark }` to mark all read. Null when there is nothing.
 *
 * WRITE ORDER, NOT `createdAt` (the review of B1). `createdAt` is when the
 * fact became true — a waiver run's due time, a draft reminder's instant — and
 * a row can be written minutes after a later one is already showing; read
 * through a time, it would be marked read on arrival, unseen.
 */
export const NotificationPage = pageOf(NotificationView).extend({
  mark: z.string().nullable(),
  /** How long the record keeps a notice: a client says so at the foot of its list. */
  kept: NoticesKept,
  /**
   * Unread rows by kind, every kind with any (§6.1): the bell's chips. Each
   * row counts once here, a folded week's rows each under their own kind.
   * Added 2026-10-18.
   */
  unreadByKind: z.partialRecord(NotificationKind, z.number().int().min(0)).default({}),
});

/** `GET /api/me/notifications?kind=`: one kind, unfolded; absent is every kind, a week folded. */
export const NotificationListQuery = z.object({
  cursor: z.string().max(400).nullable().default(null),
  kind: NotificationKind.nullable().default(null),
});
export type NotificationListQueryShape = z.infer<typeof NotificationListQuery>;
export type NotificationPageShape = z.infer<typeof NotificationPage>;

/**
 * `POST /api/me/notifications/read` — these rows, or everything up to a mark.
 *
 * `through` is *Mark all read* (PLAN-2026-09-26-bell B1): the `mark` the list
 * answered with, and every row of mine written up to it is read. A MARK, NOT
 * "ALL", so a notification written while somebody is reading the list is not
 * marked read before they have seen it. And not the ids a client holds, which
 * are one page — a count that stays at forty after "all" is not true.
 *
 * Each form is strict, so a body with both keys is refused rather than read
 * as whichever one parsed.
 */
export const MarkReadRequest = z.union([
  z.strictObject({ ids: z.array(z.string().min(1)).min(1).max(200) }),
  z.strictObject({ through: z.string().min(1).max(64) }),
]);
export const UnreadCount = z.object({ unread: z.number().int().min(0) });

const HHMM = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'HH:MM');

/** One row's switches. Absent means "the matrix default". */
export const ChannelPrefs = z.object({
  push: z.boolean().optional(),
  email: z.boolean().optional(),
});

/**
 * One matrix per person, following them across devices (NOTIFICATIONS.md §4).
 *
 * `timeZone` is the person's, because quiet hours are a wall-clock idea and
 * the server has no other way to know theirs. Null means nobody has told us,
 * and then quiet hours are simply not applied — delaying a push to "8 AM"
 * in a zone we guessed is worse than not delaying it.
 */
/**
 * How long before the week's first lock a lineup that cannot score is
 * warned about, in minutes (NOTIFICATIONS.md §4). The planner plans one
 * `notify.lineup.warn` job per value, so the list is the schedule.
 */
export const LINEUP_LEAD_MINUTES = ['30', '60', '120', '240', '720'] as const;

export const NotificationPrefs = z.object({
  rows: z.partialRecord(NotificationType, ChannelPrefs).default({}),
  quietHours: z.object({ from: HHMM, to: HHMM }).nullable().default({ from: '22:00', to: '08:00' }),
  timeZone: z.string().min(1).nullable().default(null),
  lineupLeadMinutes: z.enum(LINEUP_LEAD_MINUTES).default('120'),
  leagues: z
    .record(
      z.string(),
      z.object({ muted: z.boolean().optional(), chatToPush: z.boolean().optional() }),
    )
    .default({}),
});

/**
 * The push services a browser subscribes with (the audit of 2026-10-20). The
 * server POSTs to the endpoint it stores, so an endpoint is a URL somebody
 * chose for the server to call, and any URL was taken. Chrome, Opera and
 * Samsung Internet use Google's service, Firefox Mozilla's, Safari Apple's and
 * Edge on Windows Microsoft's; a host is one of those or under one. The names
 * kept for testing (`.test`, `.example`, `.invalid`, RFC 2606) resolve
 * nowhere, and are what the walks and the suites register.
 */
const PUSH_SERVICE_HOSTS = [
  'fcm.googleapis.com',
  'android.googleapis.com',
  'push.services.mozilla.com',
  'push.apple.com',
  'notify.windows.com',
] as const;
const TEST_NAMES = ['test', 'example', 'invalid'] as const;

/** True for an `https` endpoint on a browser's push service, or on a name kept for testing. */
export function isPushServiceEndpoint(endpoint: string): boolean {
  let url: URL;
  try {
    url = new URL(endpoint);
  } catch {
    return false;
  }
  if (url.protocol !== 'https:') return false;
  const host = url.hostname.toLowerCase();
  if (PUSH_SERVICE_HOSTS.some((h) => host === h || host.endsWith(`.${h}`))) return true;
  return (TEST_NAMES as readonly string[]).includes(host.split('.').pop() ?? '');
}

/** What `PushManager.subscribe()` hands back, as the browser serializes it. */
export const PushSubscriptionRequest = z.object({
  endpoint: z
    .url()
    .max(2048)
    .refine(isPushServiceEndpoint, 'That is not an address a browser’s push service gave it.'),
  keys: z.object({ p256dh: z.string().min(1).max(200), auth: z.string().min(1).max(100) }),
  /** A label the person would recognize: "Ken's iPhone". */
  device: z.string().max(80).nullable().default(null),
  /** True when the browser can show Safari's declarative form without a service worker. */
  declarative: z.boolean().default(false),
});
export const PushSubscriptionRef = z.object({
  id: z.string(),
  device: z.string().nullable(),
  declarative: z.boolean(),
  createdAt: z.string(),
});

/** The application server's VAPID public key, base64url, for `applicationServerKey`. */
export const VapidKey = z.object({ publicKey: z.string().min(1) });

/**
 * `GET /api/notifications/matrix` — the matrix as a preferences screen draws
 * it (NOTIFICATIONS.md §3, §4; PLAN-2026-09-26-bell B4): the groups in order,
 * and each row's words and defaults. A person's `NotificationPrefs.rows` holds
 * only what they changed, so a client needs the default to draw a switch —
 * and a second client should not have to copy the matrix to get it.
 */
export const NotificationMatrixView = z.object({
  groups: z.array(
    z.object({
      id: z.enum(['league', 'draft', 'lineup', 'moves', 'week', 'watchlist', 'achievements']),
      title: z.string(),
      rows: z.array(
        z.object({
          type: NotificationType,
          /** What happens, from the reader's side: "Someone offers you a trade". */
          label: z.string(),
          /** Breaks quiet hours and a league's mute; push, with email when there is no push. */
          lockCritical: z.boolean(),
          push: z.boolean(),
          email: z.boolean(),
        }),
      ),
    }),
  ),
});
export type NotificationMatrixViewShape = z.infer<typeof NotificationMatrixView>;

/**
 * What a stop link in an email names (`GET` and `POST /api/email/stop?k=&t=`,
 * PLAN-2026-10-18-families-and-newcomers §7.1): the row, in the words the
 * settings screen uses, and whether its email is on now. No address and no
 * name: the link may be opened by anybody the email was forwarded to.
 */
export const EmailStopView = z.object({
  type: NotificationType,
  /** The row as the settings screen names it: *The week’s recap is up*. */
  label: z.string(),
  /** Whether this row's email is on for the account the link belongs to. */
  on: z.boolean(),
});
export type EmailStopShape = z.infer<typeof EmailStopView>;

export type NotificationEventShape = z.infer<typeof NotificationEvent>;
export type NotificationViewShape = z.infer<typeof NotificationView>;
export type NotificationPrefsShape = z.infer<typeof NotificationPrefs>;
export type ChannelPrefsShape = z.infer<typeof ChannelPrefs>;
export type PushSubscriptionRequestShape = z.infer<typeof PushSubscriptionRequest>;
export type PushSubscriptionRefShape = z.infer<typeof PushSubscriptionRef>;
export type NotificationPayloadShape = z.infer<typeof NotificationPayload>;
